import { DataLayer } from '@piwikpro/react-piwik-pro';
import React from 'react';

import { graphql, Link, PageProps } from 'gatsby';

import { Button, Grid2 as Grid, styled } from '@mui/material';

import {
  isCleanroom,
  ServiceSegmentCategory,
  ServiceSegmentType,
  ServiceTeaserData,
  ServiceTeaserType,
} from '@mewa/types';
import {
  BreadCrumbData,
  BreadCrumbs,
  DownloadDefaultIcon,
  DownloadHoverIcon,
  extractTextMarking,
  IconTextButton,
  MewaCircleData,
  ResponsiveGrid,
  SeoFooter,
  ServiceTeaser,
  StageWithVideo,
} from '@mewa/ui';

import { localeBaseUrl } from '../../../libs/alternative-links';
import t from '../../../libs/translate';
import { ContactTeaserWrapper } from '../../components/contact-teaser/contact-teaser-wrapper';
import { NavigationData } from '../../components/header/types';
import Image from '../../components/image';
import { jpgPath, videoPath } from '../../helpers/asset-factory';
import { GlobalPageContext } from '../../types';
import { Collection } from '../components/collection-page/collection-page';
import { prepareBrochureSelection } from '../conversion/brochures-download/helper';
import { storeBrochures, storeServiceSegment } from '../conversion/persistence';
import { ContentSection } from '../dynamic-content-page/content-section';
import { PossibleContentSections } from '../dynamic-content-page/fragment-and-types';
import { ContentArea } from './content-area';
import { possibleComponents } from './content-section';
import { IndividualProducts } from './individual-products';

export { Head } from '../../layouts/head';

type Props = NavigationData & {
  // This is not really a collection, but we interpret the service segment data
  // as a collection for all none-clothing service segments.
  pimServiceSegment: Omit<Collection, 'products'> & {
    key: ServiceSegmentType;
    category: ServiceSegmentCategory;
    productGroups: {
      products: Collection['products'];
    }[];
    seoFooter: {
      childMarkdownRemark: { html: string };
    };
  };
  serviceTestTeaser?: ServiceTeaserData<ServiceTeaserType.cleaningClothsServiceTest>;
  strapiServiceSegment: {
    content: PossibleContentSections[];
    stageImage: string;
    stageVideo: string;
    mewaCircle: MewaCircleData | null;
  } | null;
  translations: {
    general_more_information: string;
    general_download_brochure: string;
    show_product_details_label: string;
    seo_footer_show_more_label: string;
    seo_footer_show_less_label: string;
    navigation_product_menu_label: string;
    service_segment_headline: string;
  };
};

type PageContext = GlobalPageContext;

export const query = graphql`
  query (
    $marketRegex: String!
    $id: String!
    $lang: String!
    $locale: String!
    $uuid: String
  ) {
    ...NavigationFragment

    pimServiceSegment(id: { eq: $id }) {
      name
      slug
      claim
      property
      infoText1
      infoText2
      galleryTitle
      galleryIntroText
      key
      category
      productGroups {
        products {
          ...ProductData
        }
      }
      seoFooter {
        childMarkdownRemark {
          html
        }
      }
      assets {
        keyVisualDesktop
        keyVisualMobile
      }
    }

    serviceTestTeaser: strapiServiceTeaser(
      ServiceType: { eq: "putztuch-service-test" }
      locale: { eq: $locale }
    ) {
      description: Description
      title: Title
      serviceType: ServiceType
    }

    strapiServiceSegment(locale: { eq: $locale }, pimUuid: { eq: $uuid }) {
      ...ContentSectionFragment
      stageImage
      stageVideo

      mewaCircle {
        ...MewaCircleFragment
      }
    }

    translations: translation(locale: { eq: $locale }) {
      general_more_information
      general_download_brochure
      show_product_details_label
      seo_footer_show_more_label
      seo_footer_show_less_label
      navigation_product_menu_label
      service_segment_headline
    }
  }
`;

const NonClothingServiceSegment = ({
  data: {
    pimServiceSegment: collection,
    serviceTestTeaser,
    translations,
    navigationLinks,
    contactTeaserLabels,
    headerAndFooterTranslations,
    strapiServiceSegment,
  },
  pageContext: { langPrefix, locale, lang },
}: PageProps<Props, PageContext>) => {
  const collectionData = {
    ...collection,
    description: collection.infoText1, // for non clothing service segments displayed
    // with the collections template, we use the infoText1 as the description
    infoText1: null,
    products: collection.productGroups[0]?.products ?? [],
  };

  const categoryBreadCrumb = isCleanroom(collection)
    ? {
        name: navigationLinks.navigation.cleanroom.name,
        slug: `${langPrefix}/${navigationLinks.navigation.cleanroom.slug}/`,
      }
    : {
        name: navigationLinks.navigation.nonClothing.name,
        slug: `${langPrefix}/${navigationLinks.navigation.nonClothing.slug}/`,
      };

  const breadCrumbData: BreadCrumbData[] = [
    {
      name: t(translations, 'navigation_product_menu_label'),
      slug: `${langPrefix}/${navigationLinks.navigation.productMenu.slug}/`,
    },
    categoryBreadCrumb,
    {
      name: collectionData.name,
      slug: '',
    },
  ];

  const mobileBreadCrumbData: BreadCrumbData = {
    name: navigationLinks.navigation.nonClothing.name,
    slug: `${langPrefix}/${navigationLinks.navigation.nonClothing.slug}/`,
  };

  const DescriptionList =
    collectionData.description?.split('\n')?.filter((t) => t !== '') ?? [];
  const restTextList = DescriptionList.slice(1);

  const pageTitle = extractTextMarking(`[${collectionData.name} -]`);

  const navigateToBrochuresDownload = () => {
    storeBrochures([prepareBrochureSelection(collectionData.key)]);
    DataLayer.push({ event: 'cta_brochure_download_teaser' });
  };

  const headline = t(translations, 'service_segment_headline', {
    serviceSegment: collectionData.name,
  });

  return (
    <>
      <BreadCrumbs
        data={breadCrumbData}
        mobileData={mobileBreadCrumbData}
        localeBaseUrl={localeBaseUrl(locale)}
        LinkComponent={Link}
      />
      <StageWithVideo
        title={pageTitle}
        subtitle={collectionData.claim ?? ''}
        autoPlay
        video={{
          controls: false,
          posterImage: (
            <Image
              alt=""
              images={[
                {
                  src: jpgPath(
                    strapiServiceSegment?.stageImage ?? '',
                    1920,
                    900,
                  ),
                  height: 900,
                  width: 1920,
                  media: '',
                },
              ]}
            />
          ),
          desktopSources: [
            {
              src: strapiServiceSegment?.stageVideo
                ? videoPath(strapiServiceSegment.stageVideo)
                : '',
              type: 'video/mp4',
            },
          ],
        }}
      />
      <ResponsiveGrid sx={{ marginTop: 'var(--spacer-xxxl)' }}>
        <Grid size={{ xs: 12, md: 8 }} offset={{ md: 2 }}>
          <Property>{collectionData.property}</Property>
          {DescriptionList[0] && <IntroText>{DescriptionList[0]}</IntroText>}
          {restTextList.map((t, i) => (
            <SecondaryIntroText key={i}>{t}</SecondaryIntroText>
          ))}
          {collectionData.infoText2 && (
            <SecondaryIntroText>{collectionData.infoText2}</SecondaryIntroText>
          )}
          <DownloadButtonContainer>
            <IconTextButton
              DefaultIcon={DownloadDefaultIcon}
              HoverIcon={DownloadHoverIcon}
              link={{
                component: Link,
                props: {
                  onClick: navigateToBrochuresDownload,
                  to: `${langPrefix}/${navigationLinks.conversion.brochuresDownload.slug}`,
                },
              }}
            >
              {t(translations, 'general_download_brochure')}
            </IconTextButton>
          </DownloadButtonContainer>
        </Grid>
      </ResponsiveGrid>
      <ContentArea
        headline={headline}
        style={{ marginTop: 'var(--spacer-xxxl)' }}
      >
        <IndividualProducts
          collections={[
            {
              ...collectionData,
              emotionAssetId: collectionData.assets.emotion,
            },
          ]}
          isNonClothing
          langPrefix={langPrefix}
          translations={translations}
          conversionLink={`${langPrefix}/${navigationLinks.conversion.contact.slug}`}
          onContactTeaserClick={() => {
            storeServiceSegment(collectionData.key);
          }}
          hideTeaser
        />
      </ContentArea>

      {collectionData.category !== 'MRS' && serviceTestTeaser ? (
        <ServiceTeaser
          data-pagefind-ignore
          description={serviceTestTeaser.description}
          title={serviceTestTeaser.title}
          type={ServiceTeaserType.cleaningClothsServiceTest}
          link={
            <Button
              variant="text"
              color="secondary"
              component={Link}
              to={`${langPrefix}/${navigationLinks.conversion.cleaningClothsServiceTestPage.slug}`}
            >
              {t(translations, 'general_more_information')}
            </Button>
          }
        />
      ) : null}
      {strapiServiceSegment && strapiServiceSegment.content !== null ? (
        <ContentResponsiveGrid>
          <Grid size={12}>
            <ContentSection
              possibleComponents={possibleComponents}
              conversion={{ label: '', link: '' }}
              lang={lang}
              locale={locale}
              langPrefix={langPrefix}
              sections={strapiServiceSegment.content}
              navigationLinks={navigationLinks}
              translations={{ author_box_by: '' }}
            />
          </Grid>
        </ContentResponsiveGrid>
      ) : null}

      <ContactTeaserWrapper
        contactTeaserLabels={contactTeaserLabels}
        langPrefix={langPrefix}
        navigationLinks={navigationLinks}
        serviceSegment={collection.key}
      />
      {collection.seoFooter.childMarkdownRemark.html ? (
        <SeoFooter
          html={collection.seoFooter.childMarkdownRemark.html}
          showMoreLabel={t(translations, 'seo_footer_show_more_label')}
          showLessLabel={t(translations, 'seo_footer_show_less_label')}
        />
      ) : null}
    </>
  );
};

NonClothingServiceSegment.noContactTeaser = true;

export default NonClothingServiceSegment;

const ContentResponsiveGrid = styled(ResponsiveGrid)({
  marginTop: 'var(--spacer-xxxl)',
});

const Property = styled('h3')(({ theme }) => ({
  ...theme.typography.h1,
  fontWeight: theme.typography.fontWeightMedium,
  margin: 0,

  [theme.breakpoints.up('sm')]: {
    textAlign: 'center',
  },
}));

const IntroText = styled('p')(({ theme }) => ({
  ...theme.typography.h5,
  fontWeight: theme.typography.fontWeightRegular,
  margin: 'var(--spacer-l) 0 0',

  [theme.breakpoints.up('sm')]: {
    textAlign: 'center',
  },
}));

const SecondaryIntroText = styled(IntroText)(({ theme }) => ({}));
const DownloadButtonContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  marginTop: 'var(--spacer-l)',

  [theme.breakpoints.only('xs')]: {
    marginLeft: '-16px',
  },

  [theme.breakpoints.up('sm')]: {
    justifyContent: 'center',
  },
}));
